import {
  Component,
  lazy,
  Suspense,
  useEffect,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { resolveDistrict } from "./districts";
import { StaticWorld } from "./StaticWorld";
import { WorldAtmosphere } from "./WorldAtmosphere";
import { useWorldExperience } from "./WorldExperienceProvider";

const LiteWorldRenderer = lazy(() => import("./renderer/LiteWorldRenderer"));
const BalancedWorldRenderer = lazy(
  () => import("./renderer/BalancedWorldRenderer"),
);
const HomeBalancedJourney = lazy(() => import("../home/HomeBalancedJourney"));

class VisualErrorBoundary extends Component<
  { children: ReactNode; onFailure: () => void; identity: string },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(_error: Error, _info: ErrorInfo) {
    this.props.onFailure();
  }

  override componentDidUpdate(previous: { identity: string }) {
    if (previous.identity !== this.props.identity && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function WorldBoundary() {
  const { pathname } = useLocation();
  const district = resolveDistrict(pathname);
  const { mode, recoveryAttempts, reportRendererFailure, updateDiagnostics } =
    useWorldExperience();
  const effectiveMode =
    mode === "off"
      ? "off"
      : mode === "balanced"
        ? "balanced"
        : mode === "lite" && district?.districtId === "service-pavilion"
          ? "lite"
          : "static";

  useEffect(() => {
    if (!district) return;
    updateDiagnostics({
      effectiveRouteMode: effectiveMode,
      activeDistrictId: district.districtId,
      activeSceneId: district.sceneId,
      staticImageFailed: false,
    });
  }, [district, effectiveMode, updateDiagnostics]);

  if (!district || effectiveMode === "off") return null;

  const staticFailure = () =>
    updateDiagnostics({
      effectiveRouteMode: "static",
      activeDistrictId: district.districtId,
      activeSceneId: district.sceneId,
      staticImageFailed: true,
    });

  return (
    <VisualErrorBoundary
      identity={`${district.districtId}:${effectiveMode}:${recoveryAttempts}`}
      onFailure={reportRendererFailure}
    >
      <div className="world-stage" data-district={district.districtId}>
        {effectiveMode === "balanced" ? (
          <Suspense
            fallback={
              <StaticWorld
                district={district}
                pathname={pathname}
                onFailure={staticFailure}
              />
            }
          >
            {pathname === "/" ? (
              <HomeBalancedJourney key={recoveryAttempts} />
            ) : (
              <BalancedWorldRenderer key={recoveryAttempts} />
            )}
          </Suspense>
        ) : effectiveMode === "lite" ? (
          <Suspense
            fallback={
              <StaticWorld
                district={district}
                pathname={pathname}
                onFailure={staticFailure}
              />
            }
          >
            <LiteWorldRenderer key={recoveryAttempts} />
          </Suspense>
        ) : (
          <StaticWorld
            district={district}
            pathname={pathname}
            onFailure={staticFailure}
          />
        )}
        <WorldAtmosphere district={district} />
      </div>
    </VisualErrorBoundary>
  );
}
